// @givmo/passkeys — the account's way out of a lost factor as a host hands it to `@givmo/step-up`
// (AS-WEB13, AS-WEB18): the `renderLost` both consoles' hosts supply, so "Lost your passkey?" and
// "Lost your authenticator?" open inside the "Confirm it's you" sheet, and the Sign-in & security
// card's "Can't use a passkey?" opens the same view in its own dialog.
//
//   - "Lost your passkey?" → the passkey reset (`FactorResetFlow`), the act's line said first;
//   - "Lost your authenticator?" → D-AS-21's approved B2d-lost (`LostAuthenticatorFlow`): a saved
//     recovery code, which removes the authenticator at once, or the emailed two-factor reset;
//   - a way out named directly (`wayOut`: the card's "Finish the reset", AS-WEB17) opens that one.
// The reset opens on its finish where the account's reset is armed and its wait has run (AS-WEB17).
//
// Supplied only where the reset can run: the signed-in account has an address to send a code to,
// and the account's security read is served (the reset routes are served under exactly the same
// flags, AS-BE47). Elsewhere it is `undefined`, and every lost link stays hidden.

import type { UserSecurityOut } from '@givmo/api-client';
import type { StepUpHostAdapter, StepUpOwnWayIn } from '@givmo/step-up';
import { holdsAuthenticatorFrom } from './account-factors';
import { authenticatorAppOffered } from './authenticator-eligibility';
import { FactorResetFlow } from './factor-reset-flow';
import { LostAuthenticatorFlow } from './lost-authenticator';
import { pendingFactorResetOf } from './pending-factor-reset-notice';
import type { RemovalSignOut } from './sign-out-after-removal';

export interface FactorResetLostOptions {
  /** The signed-in account's own address. */
  email: string | null | undefined;
  /** The account's served security read (`useAccountFactors().security`). */
  security: UserSecurityOut | undefined;
  /** The person's ways back in (the host adapter's `waysIn`). */
  waysIn: readonly StepUpOwnWayIn[];
  /**
   * Whether the account holds a passkey (the host's `holdsPasskey`); `undefined` while unknown. The
   * two-factor reset says it removes passkeys only where the account may hold one (AS-WEB18).
   */
  holdsPasskey?: boolean | undefined;
  /**
   * The signed-in account's uid: after a recovery code removes the authenticator, both reads are
   * fetched again for it, so a host that only observes the cache sees the change (AS-WEB18).
   */
  uid?: string | null | undefined;
  /**
   * The host's own sign-out (`useAuth().signOut`): a spent recovery code, or a finished reset that
   * takes the authenticator off, ends every session of the account (AS-BE63), so leaving the result
   * signs the console out through it.
   */
  signOut?: RemovalSignOut | undefined;
}

/** The unused recovery codes a served read reports; `undefined` when it says nothing readable. */
function recoveryCodesLeftFrom(security: UserSecurityOut): number | undefined {
  const left = (security as { mfa?: { recovery_codes_remaining?: unknown } }).mfa
    ?.recovery_codes_remaining;
  return typeof left === 'number' && Number.isFinite(left) ? left : undefined;
}

/** The host's `renderLost`, or `undefined` where the reset cannot run. */
export function factorResetRenderLost({
  email,
  security,
  waysIn,
  holdsPasskey,
  uid,
  signOut,
}: FactorResetLostOptions): StepUpHostAdapter['renderLost'] {
  if (typeof email !== 'string' || email.length === 0 || security === undefined) return undefined;
  const alsoTurnsOffTwoFactor = holdsAuthenticatorFrom(security);
  const authenticatorOffered = authenticatorAppOffered(waysIn, security);
  const recoveryCodesLeft = recoveryCodesLeftFrom(security);
  const pendingReset = pendingFactorResetOf(security);
  return function renderLost({ factor, onBack, onClose, lines, wayOut }) {
    if (factor === 'authenticator') {
      return (
        <LostAuthenticatorFlow
          email={email}
          uid={uid}
          recoveryCodesLeft={recoveryCodesLeft}
          holdsPasskey={holdsPasskey}
          authenticatorOffered={authenticatorOffered}
          pendingReset={pendingReset}
          lines={lines}
          wayOut={wayOut}
          signOut={signOut}
          {...(onBack !== undefined ? { onBack } : {})}
          onClose={onClose}
        />
      );
    }
    return (
      <FactorResetFlow
        kind="passkey"
        email={email}
        alsoTurnsOffTwoFactor={alsoTurnsOffTwoFactor}
        authenticatorOffered={authenticatorOffered}
        holdsPasskey={holdsPasskey}
        contextLine={lines?.line}
        pendingReset={pendingReset}
        uid={uid}
        signOut={signOut}
        {...(onBack !== undefined ? { onBack } : {})}
        onClose={onClose}
      />
    );
  };
}
